import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  AfterViewInit,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DayService,
  EventSettingsModel,
  MonthService,
  ScheduleComponent,
  ScheduleModule,
  WeekService,
  YearService,
} from '@syncfusion/ej2-angular-schedule';
import { loadCldr } from '@syncfusion/ej2-base';
import daGregorian from '@syncfusion/ej2-cldr-data/main/da/ca-gregorian.json';
import daNumberData from '@syncfusion/ej2-cldr-data/main/da/numbers.json';
import daTimeZoneData from '@syncfusion/ej2-cldr-data/main/da/timeZoneNames.json';
import numberingSystem from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';
import { EventDTO } from '../../../dto/event/event.dto';
import {
  EventCategoryColorsMap,
  buildEventCategoryInheritanceContext,
  normalizeEventCategoryColors,
} from '../../../services/component-data-source/data/event/event-category-color.util';
import {
  normalizeEventIsActive,
  normalizeEventIsShown,
} from '../../../services/component-data-source/data/event/event-active.util';
import {
  calculateScheduleViewDateRange,
  ScheduleViewName,
} from './events-schedule-date-range.util';
import { mapEventDtosToScheduleRows } from './events-schedule.mapper';

loadCldr(daNumberData, daTimeZoneData, daGregorian, numberingSystem);

@Component({
  selector: 'app-duna-events-schedule',
  standalone: true,
  imports: [
    CommonModule,
    ScheduleModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  providers: [DayService, WeekService, MonthService, YearService],
  templateUrl: './duna-events-schedule.component.html',
  styleUrl: './duna-events-schedule.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaEventsScheduleComponent implements OnChanges, AfterViewInit {
  @Input() events: EventDTO[] = [];
  @Input() scheduleView: ScheduleViewName = 'Month';
  @Input() selectedDate: Date = new Date();
  @Input() loading = false;
  @Input() compact = false;
  @Input() showViewToggle = true;
  @Input() categoryColors: EventCategoryColorsMap = {};

  @Output() scheduleViewChange = new EventEmitter<ScheduleViewName>();
  @Output() selectedDateChange = new EventEmitter<Date>();
  @Output() visibleRangeChange = new EventEmitter<{ startDate: string; endDate: string }>();
  @Output() eventClick = new EventEmitter<EventDTO>();

  @ViewChild('scheduleObj') scheduleObj?: ScheduleComponent;

  readonly viewOptions: {
    value: ScheduleViewName;
    label: string;
    icon: string;
  }[] = [
    { value: 'Day', label: 'Dag', icon: 'calendar_view_day' },
    { value: 'Week', label: 'Uge', icon: 'calendar_view_week' },
    { value: 'Month', label: 'Måned', icon: 'calendar_view_month' },
    { value: 'Year', label: 'År', icon: 'calendar_view_month' },
  ];

  eventSettings: EventSettingsModel = {
    dataSource: [],
    allowEditing: false,
    allowDeleting: false,
    allowAdding: false,
    fields: {
      id: 'Id',
      subject: 'Subject',
      startTime: 'StartTime',
      endTime: 'EndTime',
      isAllDay: 'IsAllDay',
    },
  } as EventSettingsModel;

  private inheritanceContext = buildEventCategoryInheritanceContext([]);

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.emitVisibleRange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events'] || changes['categoryColors']) {
      this.syncDataSource();
    }
    if (changes['scheduleView'] && this.scheduleObj) {
      this.scheduleObj.currentView = this.scheduleView;
    }
    if (changes['scheduleView'] || changes['selectedDate']) {
      this.emitVisibleRange();
    }
  }

  onViewChange(view: ScheduleViewName): void {
    this.scheduleView = view;
    this.scheduleViewChange.emit(view);
    if (this.scheduleObj) {
      this.scheduleObj.currentView = view;
    }
    this.emitVisibleRange();
    this.cdr.markForCheck();
  }

  onNavigated(event: unknown): void {
    const nav = event as { currentDate?: Date; date?: Date };
    const raw = nav?.currentDate ?? nav?.date;
    if (!raw) return;
    const d = new Date(raw);
    this.selectedDate = d;
    this.selectedDateChange.emit(d);
    this.emitVisibleRange();
  }

  onActionComplete(event: { requestType?: string }): void {
    if (event?.requestType === 'dateNavigate' && this.scheduleObj?.selectedDate) {
      const d = new Date(this.scheduleObj.selectedDate);
      this.selectedDate = d;
      this.selectedDateChange.emit(d);
      this.emitVisibleRange();
    }
  }

  onScheduleEventClick(args: { event?: { Id?: string }; cancel?: boolean }): void {
    const guid = args?.event?.Id;
    if (!guid) return;
    const match = this.events.find((e) => e.guid === guid);
    if (match) {
      args.cancel = true;
      this.eventClick.emit(match);
    }
  }

  onEventRendered(args: { data?: { CategoryColor?: string; IsActive?: boolean; IsShown?: boolean }; element?: HTMLElement }): void {
    const el = args.element;
    const data = args.data;
    if (!el || !data) return;
    el.style.setProperty('color', '#ffffff', 'important');
    el.style.setProperty('border-radius', '2px', 'important');
    el.style.setProperty('font-size', '11px', 'important');
    if (!normalizeEventIsActive(data.IsActive)) {
      el.style.setProperty('background-color', '#ccc', 'important');
      el.style.setProperty('border-color', '#bbb', 'important');
      return;
    }
    if (!normalizeEventIsShown(data.IsShown)) {
      el.style.setProperty('opacity', '0.55', 'important');
    }
    const bg = data.CategoryColor || '#445E91';
    el.style.setProperty('background-color', bg, 'important');
    el.style.setProperty('border-color', bg, 'important');
  }

  setCategoryColorsFromOrgData(raw: unknown): void {
    this.categoryColors = normalizeEventCategoryColors(raw);
    this.syncDataSource();
    this.cdr.markForCheck();
  }

  private syncDataSource(): void {
    const rows = mapEventDtosToScheduleRows(
      this.events,
      this.categoryColors,
      this.inheritanceContext
    );
    this.eventSettings = { ...this.eventSettings, dataSource: rows };
    this.cdr.markForCheck();
  }

  private emitVisibleRange(): void {
    this.visibleRangeChange.emit(
      calculateScheduleViewDateRange(this.selectedDate, this.scheduleView)
    );
  }
}
